import { useEffect, useState } from "react";
import "./Sessions.css";
import { ToastContainer, toast } from "react-toastify";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { UilTrashAlt, UilSignOutAlt } from "@iconscout/react-unicons";
import { userRequest } from "../../RequestMethods";

const sessionStatus = (session) => {
  if (session.revokedAt) return { label: "Révoquée", className: "status-revoked" };
  if (new Date(session.expiresAt) < new Date()) return { label: "Expirée", className: "status-expired" };
  return { label: "Active", className: "status-active" };
};

const SessionsTable = ({ sessions, onRevoke }) => (
  <TableContainer component={Paper} className="sessions-table-container">
    <Table>
      <TableHead>
        <TableRow className="table-header">
          <TableCell sx={{ fontWeight: "bold" }}>Connecté le</TableCell>
          <TableCell>Adresse IP</TableCell>
          <TableCell>Appareil</TableCell>
          <TableCell>Expire le</TableCell>
          <TableCell>Statut</TableCell>
          <TableCell>Action</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sessions.length === 0 && (
          <TableRow>
            <TableCell colSpan={6} align="center">
              Aucune session trouvée.
            </TableCell>
          </TableRow>
        )}
        {sessions.map((session) => {
          const status = sessionStatus(session);
          return (
            <TableRow key={session._id}>
              <TableCell>{new Date(session.issuedAt).toLocaleString("fr-FR")}</TableCell>
              <TableCell>{session.ipAddress || "—"}</TableCell>
              <TableCell className="user-agent-cell" title={session.userAgent}>
                {session.userAgent ? session.userAgent.slice(0, 40) + (session.userAgent.length > 40 ? "…" : "") : "—"}
              </TableCell>
              <TableCell>{new Date(session.expiresAt).toLocaleString("fr-FR")}</TableCell>
              <TableCell>
                <span className={`badge ${status.className}`}>{status.label}</span>
              </TableCell>
              <TableCell>
                {!session.revokedAt && (
                  <button className="action-icon-btn btn-delete" onClick={() => onRevoke(session._id)}>
                    <UilTrashAlt /> Révoquer
                  </button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

const Sessions = () => {
  const [workers, setWorkers] = useState([]);
  const [selectedWorkerId, setSelectedWorkerId] = useState("");
  const [workerSessions, setWorkerSessions] = useState([]);
  const [mySessions, setMySessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMySessions = async () => {
    try {
      const res = await userRequest.get("sessions/me");
      setMySessions(res.data.sessions || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors du chargement de vos sessions.");
    }
  };

  const fetchWorkerSessions = async (workerId) => {
    if (!workerId) {
      setWorkerSessions([]);
      return;
    }
    try {
      const res = await userRequest.get(`sessions/worker/${workerId}`);
      setWorkerSessions(res.data.sessions || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors du chargement des sessions.");
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const [workersRes] = await Promise.all([userRequest.get("workers"), fetchMySessions()]);
        setWorkers(workersRes.data.workers || []);
        setError(null);
      } catch (err) {
        setError(err?.response?.data?.message || "Erreur lors du chargement.");
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchWorkerSessions(selectedWorkerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkerId]);

  const handleRevoke = async (sessionId, isSelf) => {
    if (!window.confirm("Révoquer cette session ?")) return;
    try {
      await userRequest.delete(`sessions/${sessionId}`);
      toast.success("Session révoquée.");
      if (isSelf) fetchMySessions();
      else fetchWorkerSessions(selectedWorkerId);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors de la révocation.");
    }
  };

  const handleRevokeAll = async () => {
    if (!selectedWorkerId) return;
    if (!window.confirm("Révoquer toutes les sessions actives de cet utilisateur ? Il devra se reconnecter.")) return;

    try {
      await userRequest.post(`sessions/worker/${selectedWorkerId}/revoke-all`, {
        reason: "Déconnexion forcée par un administrateur",
      });
      toast.success("Toutes les sessions ont été révoquées.");
      fetchWorkerSessions(selectedWorkerId);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors de la révocation.");
    }
  };

  if (loading) {
    return (
      <div className="Sessions">
        <div className="loading-container">
          <div className="loading-spinner-custom"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="Sessions">
        <div className="error-message-custom">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => window.location.reload()}>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="Sessions">
      <h1>Sessions</h1>

      <section className="sessions-section">
        <h2>Mes sessions</h2>
        <SessionsTable sessions={mySessions} onRevoke={(id) => handleRevoke(id, true)} />
      </section>

      <section className="sessions-section">
        <div className="sessions-section-header">
          <h2>Sessions par utilisateur</h2>
          <div className="worker-picker">
            <select value={selectedWorkerId} onChange={(e) => setSelectedWorkerId(e.target.value)}>
              <option value="">-- Sélectionner un utilisateur --</option>
              {workers.map((worker) => (
                <option key={worker._id} value={worker._id}>
                  {worker.name} {worker.surname} ({worker.email})
                </option>
              ))}
            </select>
            {selectedWorkerId && (
              <button className="action-icon-btn btn-delete" onClick={handleRevokeAll}>
                <UilSignOutAlt /> Révoquer toutes les sessions
              </button>
            )}
          </div>
        </div>

        {selectedWorkerId ? (
          <SessionsTable sessions={workerSessions} onRevoke={(id) => handleRevoke(id, false)} />
        ) : (
          <p className="empty-hint">Sélectionnez un utilisateur pour voir ses sessions.</p>
        )}
      </section>

      <ToastContainer position="top-right" />
    </div>
  );
};

export default Sessions;
